import { useParams, useNavigate } from '@tanstack/react-router'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn, type FilterOption } from '@/lib/utils'
import { useViews, useUpdateView, useDuplicateView } from '@/hooks/use-views'
import { useViewsStore } from '@/stores/views.store'
import { useGlobalStore } from '@/stores/global-store'
import { useUserStore, selectUser } from '@/stores/user.store'
import { useTabsOverflow } from '@/hooks/use-tabs-overflow'
import { useIsMobile } from '@/hooks/use-mobile'
import { SearchInput } from '@/components/custom/search-input'
import { FiltersCombobox } from '@/components/custom/filters-combobox'
import {
  FiltersMenu,
  type ActiveFilter,
} from '@/components/custom/filters-menu'
import {
  ColumnVisibilityCombobox,
  type ColumnItem,
} from '@/components/custom/datatable/column-visibility'
import {
  CreateViewModal,
  ViewSettingsDropdown,
  DeleteViewDialog,
} from '@/components/custom/views'
import { showSuccess, showError } from '@/lib/toast'
import type {
  ViewEntityType,
  ViewListItem,
  CreateViewColumn,
  CreateViewFilter,
  CreateViewSort,
  FilterOperator,
} from '@/types/api'
import type { Tab } from '@/types/tab'
import type { SortingState } from '@tanstack/react-table'
import { useEffect, useMemo, useCallback, useState, useRef } from 'react'
import { ChevronDown, ListFilter, ArrowDownUp, Plus } from 'lucide-react'
import { useShallow } from 'zustand/react/shallow'

interface ViewTabsProps {
  /** The entity type to fetch views for */
  entityType: ViewEntityType
  /** Base path for navigation (e.g., "/companies/views") */
  basePath: string
  /** Optional class name for the container */
  className?: string
  /** Page title (shown on mobile) */
  pageTitle?: string
  /** Search term value */
  searchTerm?: string
  /** Available filters list */
  filtersList?: FilterOption[]
  /** Current filter state */
  filters?: Record<string, string[]>
  /** Current filter operators */
  filterOperators?: Record<string, FilterOperator>
  /** Total matching results */
  totalMatches?: number
  /** Called when the active view changes */
  onViewChange?: (view: ViewListItem | null) => void
  /** Called when search changes */
  onSearchChange?: (value: string) => void
  /** Called when filter changes */
  onFilterChange?: (
    filterKey: string,
    values: string[] | undefined,
    filtersGroup: number
  ) => void
  /** Called when filter operator changes */
  onFilterOperatorChange?: (filterKey: string, operator: FilterOperator) => void
  /** Clear all filters */
  clearAllFilters?: () => void
  /** Column visibility items for the visibility combobox */
  columnVisibilityItems?: ColumnItem[]
  /** Called when column visibility changes */
  onColumnVisibilityChange?: (columnId: string, isVisible: boolean) => void
  /** Called for batch column visibility changes (Show All / Hide All) */
  onBatchColumnVisibilityChange?: (changes: Record<string, boolean>) => void
  /** Called when column order changes via visibility combobox */
  onColumnOrderChange?: (newOrder: string[]) => void
  /** Current sorting state (TanStack format) */
  sorting?: SortingState
  /** Available sort options (same as filtersList for sortable fields) */
  sortsList?: FilterOption[]
  /** Called when a new sort is added */
  onSortSelect?: (sortValue: string) => void
  /** Called when an existing sort is updated */
  onSortUpdate?: (oldSort: string, newSortValue: SortingState) => void
  /** Called when sorts are reordered */
  onSortReorder?: (newSorting: SortingState) => void
  /** Clear one or all sorts */
  clearSorts?: (sortId?: string) => void
  /** Current columns configuration for creating new views */
  getCurrentColumns?: () => CreateViewColumn[]
  /** Current filters for creating new views (from working state) */
  getCurrentFilters?: () => CreateViewFilter[]
  /** Current sorts for creating new views (from working state) */
  getCurrentSorts?: () => CreateViewSort[]
  /** Whether there are unsaved changes to the view */
  hasUnsavedChanges?: boolean
  /** Callback to save the view */
  onSaveView?: () => void
  /** Whether the user can update the current view */
  canUpdateView?: boolean
  /** Whether save is in progress */
  isSaving?: boolean
  /** Callback to revert working state to saved view */
  onRevertToSaved?: () => void
}

/**
 * Reusable view tabs component that displays views as navigation tabs.
 * Uses shadcn Tabs component with route-based navigation.
 * Includes search, filters, and tab overflow handling.
 */
export function ViewTabs({
  entityType,
  basePath,
  className,
  pageTitle,
  searchTerm = '',
  filtersList = [],
  filters = {},
  filterOperators = {},
  totalMatches,
  onViewChange,
  onSearchChange,
  onFilterChange,
  onFilterOperatorChange,
  clearAllFilters,
  columnVisibilityItems,
  onColumnVisibilityChange,
  onBatchColumnVisibilityChange,
  onColumnOrderChange,
  sorting = [],
  sortsList = [],
  onSortSelect,
  onSortUpdate,
  onSortReorder,
  clearSorts,
  getCurrentColumns,
  getCurrentFilters,
  getCurrentSorts,
  hasUnsavedChanges = false,
  onSaveView,
  canUpdateView = false,
  isSaving = false,
  onRevertToSaved,
}: ViewTabsProps) {
  const navigate = useNavigate()
  const { views, isLoading } = useViews(entityType)
  const { setActiveViewId } = useViewsStore()
  const isMobile = useIsMobile()

  // Track newly added sort for auto-opening the popover
  const [newlyAddedSort, setNewlyAddedSort] = useState<string | null>(null)

  // Track newly added filter for auto-opening the popover
  const [newlyAddedFilter, setNewlyAddedFilter] = useState<string | null>(null)

  // Current user from store (internal database ID)
  const currentUser = useUserStore(selectUser)

  // Check for unsaved changes
  const checkHasUnsavedChanges = useViewsStore(
    (state) => state.hasUnsavedChanges
  )

  // Modal states
  const [createModalMode, setCreateModalMode] = useState<
    'new' | 'saveAs' | null
  >(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  // Inline edit state
  const [editingViewId, setEditingViewId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')
  const editInputRef = useRef<HTMLInputElement>(null)
  // Optimistic name updates (viewId -> optimistic name)
  const [optimisticNames, setOptimisticNames] = useState<
    Record<string, string>
  >({})

  // Mutations
  const updateView = useUpdateView(entityType)
  const duplicateView = useDuplicateView(entityType)

  // Get filter menu visibility from global store
  const { filtersMenuVisible, setFiltersMenuVisible } = useGlobalStore(
    useShallow((state) => ({
      filtersMenuVisible: state.filtersMenuVisible,
      setFiltersMenuVisible: state.setFiltersMenuVisible,
    }))
  )

  // Get viewId from route params
  const params = useParams({ strict: false })
  const viewId = (params as { viewId?: string }).viewId

  // Convert views to tabs format for overflow hook
  const tabs: Tab[] = useMemo(
    () =>
      views.map((view, index) => ({
        value: view.id,
        label: view.name,
        order: index,
      })),
    [views]
  )

  // Use overflow hook
  const { containerRef, registerWidth, setMoreRef, visible, overflowed } =
    useTabsOverflow({
      tabs,
      activeValue: viewId ?? tabs[0]?.value ?? '',
    })

  // Update store when viewId changes
  useEffect(() => {
    const newViewId = viewId ?? null
    // Get current value to avoid unnecessary updates
    const currentActiveViewId =
      useViewsStore.getState().activeViewId[entityType]

    // Only update if the value actually changed to prevent infinite loops
    if (currentActiveViewId !== newViewId) {
      setActiveViewId(entityType, newViewId)
    }
  }, [entityType, viewId, setActiveViewId])

  // Notify parent when active view changes
  useEffect(() => {
    if (onViewChange && views.length > 0) {
      const activeView = views.find((v) => v.id === viewId) ?? null
      onViewChange(activeView)
    }
  }, [viewId, views, onViewChange])

  // Handle tab/view change via navigation
  const handleViewChange = useCallback(
    (selectedViewId: string) => {
      navigate({ to: `${basePath}/${selectedViewId}` })
    },
    [navigate, basePath]
  )

  // Handle filter select
  const handleFilterSelect = useCallback(
    (filterKey: string, filtersGroup: number) => {
      setNewlyAddedFilter(filterKey)
      onFilterChange?.(filterKey, [], filtersGroup)
    },
    [onFilterChange]
  )

  // Clear newly added filter (after popover opens)
  const handleClearNewlyAddedFilter = useCallback(() => {
    setNewlyAddedFilter(null)
  }, [])

  // Handle sort select (add new sort)
  const handleSortSelect = useCallback(
    (sortValue: string) => {
      setNewlyAddedSort(sortValue)
      onSortSelect?.(sortValue)
    },
    [onSortSelect]
  )

  // Clear newly added sort (after popover opens)
  const handleClearNewlyAddedSort = useCallback(() => {
    setNewlyAddedSort(null)
  }, [])

  // Open Create View modal (save as new view from filters bar)
  const handleSaveAsNewView = useCallback(() => {
    setCreateModalMode('saveAs')
  }, [])

  // Get current active view
  const activeView = useMemo(
    () => views.find((v) => v.id === viewId) ?? null,
    [views, viewId]
  )

  // Handle view creation success - navigate to new view
  const handleCreateSuccess = useCallback(
    (newViewId: string) => {
      navigate({ to: `${basePath}/${newViewId}` })
    },
    [navigate, basePath]
  )

  // Handle duplicate
  const handleDuplicate = useCallback(async () => {
    if (!activeView) return

    try {
      const newView = await duplicateView.mutateAsync({
        sourceViewId: activeView.id,
        newName: `${activeView.name} (Copy)`,
      })
      showSuccess('View duplicated successfully')
      navigate({ to: `${basePath}/${newView.id}` })
    } catch (error) {
      console.error('Failed to duplicate view:', error)
      showError('Failed to duplicate view')
    }
  }, [activeView, duplicateView, navigate, basePath])

  // Handle delete success - navigate to first available view
  const handleDeleteSuccess = useCallback(() => {
    const remainingViews = views.filter((v) => v.id !== viewId)
    const defaultView =
      remainingViews.find((v) => v.isDefault) || remainingViews[0]
    if (defaultView) {
      navigate({ to: `${basePath}/${defaultView.id}` })
    }
  }, [views, viewId, navigate, basePath])

  // Handle inline edit - start editing on double click
  const handleStartEdit = useCallback(
    (view: ViewListItem) => {
      // Only allow editing if user owns the view and it's not global
      const isOwner = view.createdByUserId === currentUser?.id
      const isGlobal = view.type === 'GLOBAL'
      if (!isOwner || isGlobal) return

      setEditingViewId(view.id)
      setEditingName(view.name)
      // Focus input after state update
      setTimeout(() => editInputRef.current?.focus(), 0)
    },
    [currentUser?.id]
  )

  // Handle inline edit - save on blur or Enter (optimistic update)
  const handleSaveEdit = useCallback(() => {
    if (!editingViewId) return

    const trimmedName = editingName.trim()
    const originalView = views.find((v) => v.id === editingViewId)
    const originalName = originalView?.name ?? ''

    // Clear editing state immediately
    setEditingViewId(null)
    setEditingName('')

    // If name is empty or unchanged, do nothing
    if (!trimmedName || trimmedName === originalName) {
      return
    }

    // Optimistically update the UI
    setOptimisticNames((prev) => ({ ...prev, [editingViewId]: trimmedName }))

    // Make API call in background
    updateView
      .mutateAsync({
        id: editingViewId,
        data: { name: trimmedName },
      })
      .then(() => {
        // Clear optimistic state on success (React Query will update the cache)
        setOptimisticNames((prev) => {
          const next = { ...prev }
          delete next[editingViewId]
          return next
        })
      })
      .catch((error) => {
        console.error('Failed to rename view:', error)
        showError('Failed to rename view')
        // Revert optimistic update on failure
        setOptimisticNames((prev) => {
          const next = { ...prev }
          delete next[editingViewId]
          return next
        })
      })
  }, [editingViewId, editingName, views, updateView])

  // Handle key down in edit input
  const handleEditKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleSaveEdit()
      } else if (e.key === 'Escape') {
        setEditingViewId(null)
        setEditingName('')
      }
    },
    [handleSaveEdit]
  )

  // Compute active filters from filter state (include filters with empty values for pending selection)
  const activeFilters: ActiveFilter[] = useMemo(() => {
    return Object.keys(filters).map((key) => {
      const filterOption = filtersList.find((f) => f.key === key)
      return {
        filterKey: key,
        label: filterOption?.label ?? key,
        filterType: filterOption?.type,
        dataType: filterOption?.dataType ?? filterOption?.type,
        listContent: filterOption?.listContent,
      }
    })
  }, [filters, filtersList])

  const hasActiveFilters = activeFilters.length > 0
  const hasActiveSorts = sorting.length > 0
  const hasActiveFiltersOrSorts = hasActiveFilters || hasActiveSorts

  // Loading state
  if (isLoading) {
    return (
      <div className={cn('flex flex-col gap-4', className)}>
        <div className="flex gap-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-9 w-24 rounded-md" />
          ))}
        </div>
      </div>
    )
  }

  // Empty state
  if (views.length === 0) {
    return (
      <div className={cn('flex flex-col gap-4', className)}>
        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground">
            No views available
          </span>
          <Button
            variant="outline"
            size="sm"
            className="gap-1"
            onClick={() => setCreateModalMode('new')}
          >
            <Plus className="size-4" />
            Create View
          </Button>
        </div>
        {createModalMode && (
          <CreateViewModal
            open={true}
            onOpenChange={(open) => !open && setCreateModalMode(null)}
            entityType={entityType}
            mode={createModalMode}
            currentColumns={getCurrentColumns?.() ?? []}
            currentFilters={
              createModalMode === 'saveAs' ? getCurrentFilters?.() : undefined
            }
            currentSorts={
              createModalMode === 'saveAs' ? getCurrentSorts?.() : undefined
            }
            onSuccess={handleCreateSuccess}
          />
        )}
      </div>
    )
  }

  return (
    <div className={cn('flex flex-col gap-8', className)}>
      <div className="flex flex-col gap-3">
        {/* Top row: Tabs and filter controls */}
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          {/* Left: Tabs */}
          <div className="flex items-center gap-2">
            {/* Mobile: Page title + dropdown */}
            {isMobile && pageTitle && (
              <span className="text-lg font-semibold">{pageTitle}</span>
            )}
            {isMobile ? (
              // Mobile view - dropdown select
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="gap-2">
                    <span className="truncate">
                      {views.find((v) => v.id === viewId)?.name ??
                        'Select view'}
                    </span>
                    <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  {views.map((view) => {
                    const viewHasUnsavedChanges = checkHasUnsavedChanges(
                      view.id
                    )
                    return (
                      <DropdownMenuItem
                        key={view.id}
                        onClick={() => handleViewChange(view.id)}
                        className={cn(
                          'cursor-pointer',
                          view.id === viewId && 'bg-accent'
                        )}
                      >
                        <span className="flex items-center gap-1.5">
                          {optimisticNames[view.id] ?? view.name}
                          {viewHasUnsavedChanges && (
                            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
                          )}
                        </span>
                      </DropdownMenuItem>
                    )
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              // Desktop view - shadcn Tabs with overflow handling
              <div
                ref={containerRef}
                className="relative flex items-center gap-3"
              >
                <Tabs
                  value={viewId ?? views[0]?.id}
                  onValueChange={handleViewChange}
                >
                  <TabsList
                    role="navigation"
                    aria-label="Saved views"
                    aria-orientation={undefined}
                    className="gap-1 rounded-md bg-secondary/8"
                  >
                    {views.map((view) => {
                      const isVisible = visible.includes(view.id)
                      if (!isVisible) return null

                      const viewHasUnsavedChanges = checkHasUnsavedChanges(
                        view.id
                      )
                      const isEditing = editingViewId === view.id
                      const isActive = view.id === viewId
                      const isOwner = view.createdByUserId === currentUser?.id
                      const isGlobal = view.type === 'GLOBAL'
                      const canEdit = isOwner && !isGlobal

                      return (
                        <TabsTrigger
                          key={view.id}
                          value={view.id}
                          role="link"
                          aria-selected={undefined}
                          aria-controls={undefined}
                          aria-current={isActive ? 'page' : undefined}
                          ref={(el) => registerWidth(view.id, el)}
                          onDoubleClick={
                            isActive && canEdit
                              ? (e) => {
                                  e.preventDefault()
                                  e.stopPropagation()
                                  handleStartEdit(view)
                                }
                              : undefined
                          }
                        >
                          {isEditing ? (
                            <input
                              ref={editInputRef}
                              value={editingName}
                              onChange={(e) => setEditingName(e.target.value)}
                              onBlur={handleSaveEdit}
                              onKeyDown={handleEditKeyDown}
                              onClick={(e) => e.stopPropagation()}
                              className="h-auto w-auto min-w-16 max-w-32 border-none bg-transparent p-0 text-sm font-medium outline-none"
                              autoFocus
                            />
                          ) : (
                            <span className="flex items-center gap-1.5">
                              {optimisticNames[view.id] ?? view.name}
                              {viewHasUnsavedChanges && (
                                <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                              )}
                            </span>
                          )}
                        </TabsTrigger>
                      )
                    })}

                    {/* Overflow dropdown */}
                    {overflowed.length > 0 && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            ref={setMoreRef}
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-sm text-muted-foreground hover:text-foreground"
                          >
                            {overflowed.length} more...
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          {views
                            .filter((v) => overflowed.includes(v.id))
                            .map((view) => {
                              const viewHasUnsavedChanges =
                                checkHasUnsavedChanges(view.id)
                              return (
                                <DropdownMenuItem
                                  key={view.id}
                                  onClick={() => handleViewChange(view.id)}
                                  className={cn(
                                    'cursor-pointer',
                                    view.id === viewId && 'bg-accent'
                                  )}
                                >
                                  <span className="flex items-center gap-1.5">
                                    {optimisticNames[view.id] ?? view.name}
                                    {viewHasUnsavedChanges && (
                                      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
                                    )}
                                  </span>
                                </DropdownMenuItem>
                              )
                            })}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TabsList>
                </Tabs>

                {/* New View button - separate from tabs */}
                <Button
                  variant="outline"
                  className="gap-1 border-primary/20 bg-white/10 px-3 py-2 text-sm text-primary shadow-none hover:bg-primary/5 [&>svg]:text-primary"
                  onClick={() => setCreateModalMode('new')}
                >
                  <Plus className="h-4 w-4" />
                  New View
                </Button>
              </div>
            )}
          </div>
          {/* Right: Filter controls (desktop only) */}
          {!isMobile && (
            <div className="flex items-center gap-2">
              {/* View Settings */}
              {currentUser?.id && (
                <ViewSettingsDropdown
                  view={activeView}
                  currentUserId={currentUser.id}
                  onDuplicate={handleDuplicate}
                  onDelete={() => setDeleteDialogOpen(true)}
                />
              )}
              <FiltersCombobox
                triggerTitle="Filters"
                filtersList={filtersList}
                selectedFilters={Object.keys(filters).filter(
                  (k) => filters[k]?.length > 0
                )}
                onFilterSelect={handleFilterSelect}
                triggerIcon={
                  <ListFilter
                    className={cn(
                      'h-3.5 w-3.5',
                      hasActiveFilters
                        ? 'text-primary'
                        : 'text-muted-foreground'
                    )}
                  />
                }
                className={cn(hasActiveFilters && 'border-primary')}
                hasAppliedFilters={hasActiveFilters}
                isFiltersMenuVisible={filtersMenuVisible}
                onFiltersMenuVisibleChange={setFiltersMenuVisible}
              />
              <FiltersCombobox
                triggerTitle="Sort"
                searchPlaceholder="Sort By"
                emptyMessage="No sort found."
                filtersList={sortsList}
                selectedFilters={sorting.map((s) => s.id)}
                onFilterSelect={handleSortSelect}
                triggerIcon={
                  <ArrowDownUp
                    className={cn(
                      'h-3.5 w-3.5',
                      sorting.length > 0
                        ? 'text-primary'
                        : 'text-muted-foreground'
                    )}
                  />
                }
                className={cn(sorting.length > 0 && 'border-primary')}
                hasAppliedFilters={hasActiveSorts}
                isFiltersMenuVisible={filtersMenuVisible}
                onFiltersMenuVisibleChange={setFiltersMenuVisible}
              />
            </div>
          )}
        </div>
        {/* Active filters and sorts row - also show when hasUnsavedChanges so user can save after clearing */}
        {(hasActiveFiltersOrSorts || hasUnsavedChanges) &&
          clearAllFilters &&
          filtersMenuVisible && (
            <FiltersMenu
              activeFilters={activeFilters}
              filtersList={filtersList}
              filterValues={filters}
              filterOperators={filterOperators}
              sorting={sorting}
              sortsList={sortsList}
              newlyAddedFilter={newlyAddedFilter}
              newlyAddedSort={newlyAddedSort}
              totalMatches={totalMatches}
              clearAllFilters={clearAllFilters}
              clearSorts={clearSorts}
              onFilterValueChange={(filterKey, value) => {
                if (Array.isArray(value)) {
                  onFilterChange?.(filterKey, value as string[], 0)
                } else if (value !== undefined && value !== null) {
                  onFilterChange?.(filterKey, [String(value)], 0)
                } else {
                  onFilterChange?.(filterKey, [], 0)
                }
              }}
              onFilterOperatorChange={(filterKey, operator) => {
                onFilterOperatorChange?.(filterKey, operator)
              }}
              onFilterRemove={(filterKey) => {
                onFilterChange?.(filterKey, undefined, 0)
              }}
              onFilterSelect={handleFilterSelect}
              onClearNewlyAddedFilter={handleClearNewlyAddedFilter}
              onSortSelect={handleSortSelect}
              onSortUpdate={onSortUpdate}
              onSortReorder={onSortReorder}
              onClearNewlyAddedSort={handleClearNewlyAddedSort}
              hasUnsavedChanges={hasUnsavedChanges}
              onSaveView={onSaveView}
              canUpdateView={canUpdateView}
              isSaving={isSaving}
              onRevertToSaved={onRevertToSaved}
              onSaveAsNewView={handleSaveAsNewView}
            />
          )}
      </div>
      {/* Search row with column visibility */}
      <div className="flex items-center justify-between gap-2">
        <SearchInput
          placeholder="Search"
          value={searchTerm}
          onChange={onSearchChange}
          className="w-full md:w-[280px]"
        />
        {/* Column visibility */}
        {columnVisibilityItems &&
          onColumnVisibilityChange &&
          onColumnOrderChange && (
            <ColumnVisibilityCombobox
              columns={columnVisibilityItems}
              onVisibilityChange={onColumnVisibilityChange}
              onBatchVisibilityChange={onBatchColumnVisibilityChange}
              onOrderChange={onColumnOrderChange}
            />
          )}
      </div>

      {/* View Management Modals - only compute current state when modal is open */}
      {createModalMode && (
        <CreateViewModal
          open={true}
          onOpenChange={(open) => !open && setCreateModalMode(null)}
          entityType={entityType}
          mode={createModalMode}
          currentColumns={getCurrentColumns?.() ?? []}
          currentFilters={
            createModalMode === 'saveAs' ? getCurrentFilters?.() : undefined
          }
          currentSorts={
            createModalMode === 'saveAs' ? getCurrentSorts?.() : undefined
          }
          onSuccess={handleCreateSuccess}
        />
      )}

      {activeView && (
        <DeleteViewDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          viewId={activeView.id}
          viewName={activeView.name}
          entityType={entityType}
          onSuccess={handleDeleteSuccess}
        />
      )}
    </div>
  )
}

/**
 * Hook to get the current view ID from route params,
 * with fallback to default view
 */
export function useCurrentViewId(entityType: ViewEntityType): string | null {
  const params = useParams({ strict: false })
  const viewId = (params as { viewId?: string }).viewId
  const { getDefaultViewId } = useViewsStore()

  // Return viewId from params, or fallback to default
  return viewId ?? getDefaultViewId(entityType)
}
